<template>
  <div class="upcoming-events container">
    <div class="upcoming-events-wrapper">
      <upcoming-events-list
        ref="eventList"
        :server-items="availableEvents"
        variant="upcoming-events"
      >
        <template #filter>
          <div class="upcoming-events-header">
            <div class="range-date-filter form">
              <div class="input date-range">
                <date-range-field-input
                  ref="dateRangeField"
                  :lang="calendarLang"
                  :min-date="todaysDate"
                  :max-date="oneYearLaterDate"
                  :available-dates="availableDates"
                  @date-change="handleDateChange"
                >
                  <template slot-scope="{ toggleCalendar, resultLabel }">
                    <input
                      id="range-date-events"
                      type="text"
                      :value="resultLabel"
                      name="range-date-events"
                      :max="oneYearLaterDate"
                      data-field-type="DateField"
                      readonly
                      @click.prevent="toggleCalendar"
                    >
                    <label
                      for="range-date-events"
                      class="floating"
                      :class="resultLabel ? '' : 'no-result'"
                    >
                      {{ resultLabel ? tc('calendar.dateRange') : tc('calendar.selectDateRange') }}
                    </label>
                    <button
                      class="icon-calendar"
                      @click.prevent="toggleCalendar"
                    />
                  </template>
                </date-range-field-input>
              </div>
            </div>
            <upcoming-events-filter
              v-if="showEventsFilter"
              ref="upcomingEventsFilter"
              :events="sortedEvents"
              :date-range="selectedDates"
              :settings="settings"
              :dictionary="dictionary"
              @updateFilteredEvents="updateAvailableEvents"
              @resetDateRange="handleResetDateRange"
            >
              <button
                slot-scope="{
                  activate,
                  isActive,
                  resetAllFilters,
                  totalFiltersSelected,
                }"
                class="filter-sidebar--activator"
                :class="totalFiltersSelected ? 'filter-selected' : ''"
                @click="activate"
              >
                <span
                  class="filter-sidebar--activator-label"
                  :class="totalFiltersSelected ? 'filter-selected' : ''"
                >{{ dictionary['upcoming-events:filter'] }}</span>
                <span
                  v-if="totalFiltersSelected > 0"
                  class="filter-sidebar--activator-total-tag"
                >{{ totalFiltersSelected }}</span>
                <span class="icon-filter-black" />
                <button
                  v-if="totalFiltersSelected > 0"
                  :tabindex="isActive ? '-1' : '0'"
                  class="filter-sidebar--activator-reset"
                  @click.stop="resetAllFilters"
                >
                  {{ dictionary['upcoming-events:reset'] }}
                </button>
              </button>
            </upcoming-events-filter>
          </div>
        </template>
        <template #no-items>
          <div class="error-box no-margin">
            <span>
              {{ dictionary['upcoming-events:no-result-message'] }}
            </span>
          </div>
        </template>
        <template #card-template="{ item, key }">
          <event-card
            :key="key"
            :event="item"
            :dictionary="dictionary"
          />
        </template>
        <template #pagination="{ pagination, defaultLimit, allLoaded }">
          <div class="pagination">
            <load-more-pagination
              v-model="pagination"
              variant="upcoming-events"
              v-bind="{ defaultLimit, allLoaded }"
              :load-more-text="dictionary['upcoming-events:load-more']"
            />
          </div>
        </template>
      </upcoming-events-list>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import { mapState, mapGetters } from 'vuex'
  import UpcomingEventsFilter from '@components/01.base/upcoming-events/upcoming-events-filter.vue'
  import EventCard from '@components/01.base/upcoming-events/event-card.vue'
  import LoadMorePagination from '@components/01.base/02.lists/02.pagination-types/load-more-pagination.vue'
  import UpcomingEventsList from '@components/01.base/upcoming-events/upcoming-events-list.vue'
  import DateRangeFieldInput from '@components/01.base/01.forms/inputs/date-range-field-input.vue'

  export default {
    name: 'UpcomingEvents',
    components: {
      LoadMorePagination,
      EventCard,
      UpcomingEventsFilter,
      UpcomingEventsList,
      DateRangeFieldInput
    },
    props: {
      events: {
        type: Array,
        required: true,
        default: () => ([])
      },
      dictionary: {
        type: Object,
        required: true,
        default: () => ({})
      },
      settings: {
        type: Object,
        default: () => ({})
      },
      calendarLang: {
        type: Object,
        default: () => ({})
      },
    },
    data() {
      return {
        availableEvents: [],
        calendarAvailableEvents: [],
        sortedEvents: [],
        availableDates: [],
        isOpen: false,
        selectedDates: {},
      }
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width', wHeight: 'height' }),
      ...mapGetters('i18n', ['tc']),
      showEventsFilter() {
        const allFiltersDisabled = !this.settings.showEventTypeFilter
          && !this.settings.showComplimentaryFilter
          && !this.settings.showFanOfMOFilter

        return this.events && this.events.length > 0 && !allFiltersDisabled
      },
      todaysDate() {
        return moment().format('YYYY-MM-DD')
      },

      oneYearLaterDate() {
        return moment().add(1, 'year').format('YYYY-MM-DD')
      },
    },
    watch: {
      availableEvents: {
        handler(val) {
          this.$refs.eventList.loadItems({ items: val, total: val.length })
        },
        deep: true,
      },
      calendarAvailableEvents(val) {
        this.generateAvailableDates(val)
      }
    },
    created() {
      this.initializeEvents()
    },

    methods: {
      initializeEvents() {
        this.sortedEvents = this.filterAndSortEvents(this.events)
        this.availableEvents = this.sortedEvents
        this.calendarAvailableEvents = this.sortedEvents
      },
      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.isOpen = false
      },

      handleOpenModal() {
        document.body.classList.add('no-scroll')
        this.isOpen = true
      },

      updateAvailableEvents(filteredEvents, filteredCalendarEvents) {
        this.availableEvents = filteredEvents
        this.calendarAvailableEvents = filteredCalendarEvents
      },
      filterAndSortEvents(events) {
        const now = moment()

        const futureEvents = events.filter((event) => {
          return moment(event.eventStartDate, 'M/D/YYYY').isSameOrAfter(now, 'day')
        })

        futureEvents.sort((a, b) => {
          const dateA = moment(a.eventStartDate, 'M/D/YYYY')
          const dateB = moment(b.eventStartDate, 'M/D/YYYY')
          return dateA - dateB
        })

        return futureEvents
      },
      handleDateChange(date) {
        this.selectedDates = date
      },
      generateAvailableDates(events) {
        const dates = events.map((event) => event.eventStartDate)

        this.availableDates = [...new Set(dates)]
      },
      handleResetDateRange() {
        this.$refs.dateRangeField.resetCalendarDate()
      }
    }
  }
</script>
